/**
 * Adaline "כמה עולה פרסום?" — subscription plan cards.
 *
 * GET client-api/subscriptions/plans/ returns ONE ROW PER (tier, billing
 * period): Go monthly, Go yearly, Plus monthly, ... This file folds those rows
 * into one tier per plan name so the cards never show a tier twice, and does
 * all the money maths in one place (kept identical to the Flutter copy in
 * market_app lib/widgets/chatbox/adaline_plan_cards.dart).
 *
 * Nothing here is hardcoded: names, prices, allowances and benefits all come
 * from the API rows.
 */

export type BillingPeriod = 'MONTHLY' | 'YEARLY';

/** Shape of one row of client-api/subscriptions/plans/ (SubscriptionPlanSerializer). */
export interface ApiPlan {
  id: number;
  name: string;
  description: string | null;
  price: string | number;            // DecimalField -> "49.00"
  billing_period: string;            // 'MONTHLY' | 'YEARLY'
  duration_days?: number;
  image?: string | null;             // may be null - never required for rendering
  sort_order: number | null;
  max_concurrent_ads: number | null; // null = unlimited
}

export interface PlanTier {
  key: string;                       // lower-cased plan name
  name: string;                      // display name (Go / Plus / Pro)
  sortOrder: number;
  monthly: ApiPlan | null;
  yearly: ApiPlan | null;
}

export function periodOf(p: ApiPlan): BillingPeriod | null {
  const v = (p.billing_period || '').toUpperCase();
  return v === 'MONTHLY' || v === 'YEARLY' ? v : null;
}

export function priceOf(p: ApiPlan | null | undefined): number | null {
  if (!p) return null;
  const n = typeof p.price === 'number' ? p.price : parseFloat(p.price);
  return Number.isFinite(n) ? n : null;
}

/**
 * Rows -> tiers. Grouped by plan NAME (case-insensitive), not by sort_order,
 * so two tiers that happen to share a sort_order still stay separate.
 * Ordered by sort_order (lowest of the tier's rows), then monthly price, then name.
 */
export function buildPlanTiers(rows: ApiPlan[]): PlanTier[] {
  const byName = new Map<string, PlanTier>();
  for (const row of rows || []) {
    const period = periodOf(row);
    const name = (row?.name || '').trim();
    if (!period || !name) continue;
    const key = name.toLowerCase();
    let tier = byName.get(key);
    if (!tier) {
      tier = { key, name, sortOrder: Number.MAX_SAFE_INTEGER, monthly: null, yearly: null };
      byName.set(key, tier);
    }
    if (row.sort_order != null) tier.sortOrder = Math.min(tier.sortOrder, row.sort_order);
    // Keep the first row per period (the API orders by sort_order, price).
    if (period === 'MONTHLY' && !tier.monthly) tier.monthly = row;
    if (period === 'YEARLY' && !tier.yearly) tier.yearly = row;
  }
  return [...byName.values()].sort((a, b) =>
    a.sortOrder - b.sortOrder
    || (priceOf(a.monthly ?? a.yearly) ?? 0) - (priceOf(b.monthly ?? b.yearly) ?? 0)
    || a.name.localeCompare(b.name));
}

export function planFor(tier: PlanTier, period: BillingPeriod): ApiPlan | null {
  return period === 'MONTHLY' ? tier.monthly : tier.yearly;
}

/** Yearly price / 12 - what a yearly subscriber effectively pays per month. */
export function monthlyEquivalent(tier: PlanTier): number | null {
  const y = priceOf(tier.yearly);
  return y == null ? null : y / 12;
}

/** Monthly price x 12 - yearly price. Null when either row is missing or there is no saving. */
export function yearlySaving(tier: PlanTier): number | null {
  const m = priceOf(tier.monthly);
  const y = priceOf(tier.yearly);
  if (m == null || y == null) return null;
  const saving = Math.round((m * 12 - y) * 100) / 100;
  return saving > 0 ? saving : null;
}

/** Benefits = one per non-empty line of `description` (leading bullet marks stripped). */
export function benefitsOf(p: ApiPlan | null): string[] {
  if (!p?.description) return [];
  return p.description
    .split(/\r\n|\r|\n/)
    .map(l => l.replace(/^[\s•\-–*·]+/, '').trim())
    .filter(Boolean);
}

export function allowanceOf(p: ApiPlan | null): string {
  if (!p) return '';
  return p.max_concurrent_ads == null
    ? 'מודעות פעילות ללא הגבלה'
    : `${p.max_concurrent_ads} מודעות פעילות במקביל`;
}

/** ₪1,908 / ₪39 / ₪12.50 - shekel sign first, thousands separator, no ".00". */
export function formatIls(value: number | null): string {
  if (value == null) return '';
  const rounded = Math.round(value * 100) / 100;
  const s = rounded.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `₪${s}`;
}

// ── Per-ad plans (AdsRoute: מודעה אפורה / מודעה מודגשת / SUPER GOLD) ──────────
// GET client-api/ads-routes/ -> {count, next, previous, results:[...]}.
// Paid once per ad, chosen at the end of the publish form.

export interface ApiAdRoute {
  id: number;
  title: string;
  description: string | null;
  price: string | number;      // 0 = free
  lifeSpan: number | null;     // days the ad stays active
  image?: string | null;       // not used by the cards
  level: string | null;        // LEVEL1 / LEVEL2 / LEVEL3
  blocked?: boolean;           // logged-in user used up the free quota (LEVEL1 only)
}

function levelRank(level: string | null | undefined): number {
  const m = /(\d+)/.exec(level || '');
  return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
}

/** Ordered by level (LEVEL1, 2, 3), then price, then id. Rows without a usable price are dropped. */
export function sortAdRoutes(rows: ApiAdRoute[]): ApiAdRoute[] {
  const price = (r: ApiAdRoute) => (typeof r.price === 'number' ? r.price : parseFloat(r.price));
  return (rows || [])
    .filter(r => r && (r.title || '').trim() && Number.isFinite(price(r)))
    .sort((a, b) => levelRank(a.level) - levelRank(b.level) || price(a) - price(b) || a.id - b.id);
}

export function adRoutePrice(r: ApiAdRoute): number {
  return typeof r.price === 'number' ? r.price : parseFloat(r.price);
}

export function adRouteBenefits(r: ApiAdRoute): string[] {
  if (!r.description) return [];
  return r.description
    .split(/\r\n|\r|\n/)
    .map(l => l.replace(/^[\s•\-–*·]+/, '').trim())
    .filter(Boolean);
}

export function lifeSpanLabel(r: ApiAdRoute): string {
  return r.lifeSpan ? `${r.lifeSpan} ימים פעיל` : '';
}
